import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import agreementsAPI, { AgreementStatus, apiErrorMessage } from '../../services/agreementsAPI';
import { acceptanceCheckboxText } from '../../utils/agreementText';
import { btnClass } from '../../common/buttonClasses';
import styles from './TermsUpdateNotice.module.css';

const DISMISS_PREFIX = 'termsNoticeDismissed:';

function wasDismissed(key: string): boolean {
    try {
        return sessionStorage.getItem(DISMISS_PREFIX + key) === '1';
    } catch {
        return false;
    }
}

function dismiss(key: string) {
    try {
        sessionStorage.setItem(DISMISS_PREFIX + key, '1');
    } catch {
        // Storage blocked: the notice simply comes back next page load.
    }
}

/**
 * Org admins only (the server sends these fields only to them):
 * - during a new Terms version's 30-day notice period, a notice strip (checklist §6), drawn in the
 *   page area under the header (`part="strip"`, from SidebarLayout);
 * - after it takes effect, a prompt to re-accept, which records a new acceptance (`part="dialog"`,
 *   from the agreement gate). Continued use already counts as acceptance under §16.5, so the prompt
 *   can be put off for the session.
 */
const TermsUpdateNotice: React.FC<{ status: AgreementStatus; entityId: string; part: 'dialog' | 'strip' }> = ({ status, entityId, part }) => {
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const [, force] = useState(0);
    const [title, setTitle] = useState('');
    const [agreed, setAgreed] = useState(false);
    const reaccept = status.reaccept;
    const upcoming = status.upcomingTerms;

    const submit = useMutation({
        mutationFn: () =>
            agreementsAPI.reacceptTerms({
                acceptorTitle: title.trim(),
                agreed,
                acceptanceText: acceptanceCheckboxText({
                    clientLegalName: reaccept?.legalName || '',
                    tosVersion: reaccept?.version || '',
                    dpaVersion: reaccept?.dpaVersion ?? null,
                }),
                clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agreement-status', entityId] }),
    });

    if (part === 'dialog' && reaccept?.required && reaccept.version && !wasDismissed(`reaccept:${reaccept.version}`)) {
        const text = acceptanceCheckboxText({
            clientLegalName: reaccept.legalName || '',
            tosVersion: reaccept.version,
            dpaVersion: reaccept.dpaVersion ?? null,
        });
        return (
            <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="terms-reaccept-title">
                <div className={styles.dialog}>
                    <h2 id="terms-reaccept-title" className={styles.title}>
                        {t('termsUpdate.reacceptTitle', 'Updated Terms of Service')}
                    </h2>
                    <p>
                        {t('termsUpdate.reacceptBody', 'Version {{version}} of the Aptive Index Terms of Service is now in effect. Please review and accept it for your organization.', {
                            version: reaccept.version,
                        })}
                    </p>
                    <p>
                        <a href={reaccept.url} target="_blank" rel="noreferrer" className={styles.link}>
                            {t('termsUpdate.read', 'Read the Terms of Service (v{{version}})', { version: reaccept.version })}
                        </a>
                    </p>
                    <label className={styles.field}>
                        <span>{t('termsUpdate.yourTitle', 'Your title')}</span>
                        <input value={title} onChange={(e) => setTitle(e.target.value)} />
                    </label>
                    {/* The acceptance statement stays in English: it is the exact text recorded with the acceptance. */}
                    <label className={styles.check}>
                        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                        <span>{text}</span>
                    </label>
                    {submit.isError && <p className="form-error-message">{apiErrorMessage(submit.error)}</p>}
                    <div className={styles.actions}>
                        <button
                            type="button"
                            className={btnClass({ variant: 'primary' })}
                            disabled={!agreed || !title.trim() || submit.isPending}
                            onClick={() => submit.mutate()}
                        >
                            {t('termsUpdate.accept', 'I Accept')}
                        </button>
                        <button
                            type="button"
                            className={btnClass({ variant: 'secondary' })}
                            onClick={() => {
                                dismiss(`reaccept:${reaccept.version}`);
                                force((n) => n + 1);
                            }}
                        >
                            {t('termsUpdate.later', 'Remind me later')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (part === 'strip' && upcoming && !wasDismissed(`upcoming:${upcoming.version}`)) {
        const date = new Date(upcoming.effectiveDate).toLocaleDateString(i18n.language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC',
        });
        return (
            <div className={styles.strip} role="status">
                <span>
                    {t('termsUpdate.upcoming', 'Updated Terms of Service (v{{version}}) take effect {{date}}.', { version: upcoming.version, date })}{' '}
                    <a href={upcoming.url} target="_blank" rel="noreferrer" className={styles.link}>
                        {t('termsUpdate.whatChanged', "See what's changing")}
                    </a>
                </span>
                <button
                    type="button"
                    className={btnClass({ variant: 'secondary', size: 'sm' })}
                    onClick={() => {
                        dismiss(`upcoming:${upcoming.version}`);
                        force((n) => n + 1);
                    }}
                >
                    {t('termsUpdate.dismiss', 'Dismiss')}
                </button>
            </div>
        );
    }
    return null;
};

export default TermsUpdateNotice;
